import React, { useState, useEffect } from "react";
import { db, storage, serverTimestamp } from "../../../firebaseClient";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../Toast";
import { DateField } from "../form/DateField";
import { formatDate, parseDate } from "../../utils/dateUtils";
import { analyzeUploadedFile } from "../../services/aiService";
import { uploadTempFile } from "../../services/storageUpload";
import { FileUploadKit } from "../common/FileUploadKit";

interface LitigationDoc {
  id: string;
  title: string; // Added field
  fileName: string;
  filePath: string;
  downloadUrl: string;
  docDate: string;
  summary: string;
  createdAt: any;
  createdBy: string;
  size?: number;
}

interface Props {
  matterId: string;
  canEdit: boolean;
}

export function LitigationDocsPanel({ matterId, canEdit }: Props) {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [docs, setDocs] = useState<LitigationDoc[]>([]);
  const [loading, setLoading] = useState(true);

  // Upload Form State
  const [docTitle, setDocTitle] = useState(""); // New field
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [docDate, setDocDate] = useState(formatDate(new Date()));
  const [summary, setSummary] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);

  useEffect(() => {
    if (!matterId) return;
    const unsub = db.collection("legal_matters")
      .doc(matterId)
      .collection("litigationDocs")
      .orderBy("createdAt", "desc")
      .onSnapshot((snap) => {
        setDocs(snap.docs.map(d => ({ id: d.id, ...d.data() } as LitigationDoc)));
        setLoading(false);
      });
    return () => unsub();
  }, [matterId]);

  const handleAiSummary = async () => {
    if (!selectedFile) {
      alert("請先選擇檔案");
      return;
    }

    setIsAiLoading(true);
    try {
      const tempMeta = await uploadTempFile(selectedFile);
      const result = await analyzeUploadedFile({
        files: [selectedFile],
        fileMeta: tempMeta,
        purpose: "litigation_document_summary",
        mode: "summary",
        userInstruction:
          "請以『務實法務版』+『管理報告版』兩段式輸出：\n\nA. 務實法務版：先交代文件類型/事實摘要/爭點/關鍵時間點/金額與主張，再列出可行處置與待補資料。\nB. 管理報告版：用 5-8 句話給主管看（狀態、風險、下一步、需要資源/決策）。\n\n最後請提供可回填欄位建議值：title/mainDate/summary/amount。"
      });

      if (!result.ok) {
        showToast(result.message || "AI 摘要失敗", "error");
        return;
      }

      const fields: any = (result as any)?.fields || (result as any)?.extractedFields || {};

      // 保守回填：只填空欄位
      if (!docTitle.trim()) {
        const t = fields.title || fields.docTitle || fields.name;
        if (t) setDocTitle(String(t));
      }
      if (!docDate.trim()) {
        const d = fields.mainDate || fields.date || fields.documentDate;
        if (d) setDocDate(String(d));
      }

      if (result.summary) {
        setSummary(result.summary);
        showToast("AI 摘要生成完畢");
      }
    } catch (e: any) {
      console.error(e);
      showToast("AI 解析失敗: " + e.message, "error");
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !docDate || !summary.trim() || !docTitle.trim()) {
      alert("請完整填寫文件標題、檔案、日期與摘要");
      return;
    }

    setIsUploading(true);
    try {
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const storagePath = `litigationDocs/${matterId}/${ts}_${selectedFile.name}`;
      const ref = storage.ref(storagePath);
      
      await ref.put(selectedFile);
      const downloadUrl = await ref.getDownloadURL();

      await db.collection("legal_matters")
        .doc(matterId)
        .collection("litigationDocs")
        .add({
          title: docTitle.trim(),
          fileName: selectedFile.name,
          filePath: storagePath,
          downloadUrl,
          docDate,
          summary,
          createdAt: serverTimestamp(),
          createdBy: user?.email || "unknown",
          size: selectedFile.size
        });

      showToast("上傳成功");
      setDocTitle("");
      setSelectedFile(null);
      setSummary("");
    } catch (e: any) {
      console.error(e);
      showToast("上傳失敗: " + e.message, "error");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Upload Panel */}
      {canEdit && (
        <div className="section" style={{ background: 'rgba(255,255,255,0.03)', padding: 16 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 12, color: '#93c5fd', fontSize: 14 }}>📤 上傳新訴訟文件 / 附件</div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 16 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label className="label">文件標題 *</label>
              <input 
                className="input" 
                placeholder="例如：原告起訴狀、證人陳報狀(一)..." 
                value={docTitle}
                onChange={e => setDocTitle(e.target.value)}
                disabled={isUploading || isAiLoading}
              />
            </div>
            <DateField 
              label="文件日期 *" 
              value={parseDate(docDate)} 
              onChange={d => setDocDate(formatDate(d))} 
              disabled={isUploading || isAiLoading}
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <FileUploadKit 
              label="上傳檔案"
              description="點擊選擇或拖曳檔案至此區域"
              multiple={false}
              onFilesSelected={files => setSelectedFile(files[0])}
              rawFiles={selectedFile ? [selectedFile] : []}
              onRemoveRawFile={() => setSelectedFile(null)}
              disabled={isUploading || isAiLoading}
            />
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label className="label" style={{ marginBottom: 0 }}>摘要描述 *</label>
              <button
                type="button"
                onClick={handleAiSummary}
                disabled={!selectedFile || isAiLoading || isUploading}
                style={{
                  fontSize: 12,
                  padding: '4px 14px',
                  color: '#fff',
                  background: (!selectedFile || isAiLoading) ? '#444' : 'linear-gradient(to right, #A447FF, #FF5CC4)',
                  border: 'none',
                  borderRadius: '999px',
                  cursor: (!selectedFile || isAiLoading) ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontWeight: 500,
                  transition: 'transform 0.1s ease',
                  opacity: (!selectedFile || isAiLoading) ? 0.6 : 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                }}
              >
                <span>✨</span> {isAiLoading ? "處理中..." : "AI摘要"}
              </button>
            </div>
            <textarea 
              className="input" 
              rows={3} 
              placeholder="請輸入文件摘要重點..." 
              value={summary}
              onChange={e => setSummary(e.target.value)}
              disabled={isUploading || isAiLoading}
            />
          </div>

          <div className="row" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
            <button 
              className="btn" 
              style={{ width: 'auto', background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 24px' }}
              onClick={handleUpload}
              disabled={!selectedFile || !summary.trim() || !docTitle.trim() || isUploading || isAiLoading}
            >
              {isUploading ? "上傳中..." : "開始上傳"}
            </button>
          </div>
        </div>
      )}

      {/* List Panel - 歷史清單區 */}
      <div className="section" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', background: '#222', borderBottom: '1px solid #333', fontWeight: 'bold', fontSize: 14 }}>
            📋 已上傳訴訟文件歷史紀錄
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#1a1a1a', color: '#aaa', textAlign: 'left' }}>
            <tr>
              <th style={{ padding: 12, width: 110 }}>文件日期</th>
              <th style={{ padding: 12, width: 200 }}>文件標題</th>
              <th style={{ padding: 12 }}>摘要內容</th>
              <th style={{ padding: 12, width: 80, textAlign: 'right' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center' }}>載入中...</td></tr>}
            {!loading && docs.length === 0 && <tr><td colSpan={4} style={{ padding: 30, textAlign: 'center', color: '#666' }}>尚無上傳紀錄</td></tr>}
            {!loading && docs.map(doc => (
              <tr key={doc.id} style={{ borderBottom: '1px solid #333' }}>
                <td style={{ padding: 12, color: '#aaa' }}>{doc.docDate}</td>
                <td style={{ padding: 12, fontWeight: 'bold', color: '#fff' }}>{doc.title}</td>
                <td style={{ padding: 12, color: '#ddd' }}>
                    <div style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.summary}>
                        {doc.summary}
                    </div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>{doc.fileName}</div>
                </td>
                <td style={{ padding: 12, textAlign: 'right' }}>
                  <a href={doc.downloadUrl} target="_blank" rel="noreferrer" className="smallbtn" style={{ textDecoration: 'none' }}>查看 / 下載</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
